import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  CheckCircle,
  Loader2,
  Trash2,
  RotateCcw,
  Send,
  CalendarCheck,
  CalendarClock,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingsList = ({ user }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({ pending_count: 0, completed_count: 0, total: 0 });
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchBookings();
  }, [activeTab]);

  const fetchBookings = async () => {
    try {
      let url = `${BACKEND_URL}/api/admin/bookings?limit=100`;
      if (activeTab === 'pending') {
        url += '&status=confirmed';
      } else if (activeTab === 'completed') {
        url += '&status=completed';
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
        setStats({
          pending_count: data.pending_count || 0,
          completed_count: data.completed_count || 0,
          total: data.total || 0
        });
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (bookingId, sendEmail = true) => {
    setProcessingId(bookingId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}/complete?send_email=${sendEmail}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.email_sent) {
          setSuccessMessage('Booking completed & thank you email sent!');
        } else if (data.email_error) {
          setSuccessMessage(`Booking completed. Email failed: ${data.email_error}`);
        } else {
          setSuccessMessage('Booking marked as completed');
        }
        setTimeout(() => setSuccessMessage(''), 4000);
        fetchBookings();
      }
    } catch (error) {
      console.error('Error completing booking:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReopen = async (bookingId) => {
    setProcessingId(bookingId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}/reopen`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        setSuccessMessage('Booking reopened');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchBookings();
      }
    } catch (error) {
      console.error('Error reopening booking:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    setProcessingId(bookingId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setSuccessMessage('Booking deleted');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchBookings();
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Booking Consultations</h1>
              <p className="text-gray-400 text-sm">Manage client consultation bookings</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {successMessage}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <CalendarCheck className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-gray-500 text-sm">Total Bookings</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <CalendarClock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending_count}</p>
                <p className="text-gray-500 text-sm">Pending</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.completed_count}</p>
                <p className="text-gray-500 text-sm">Completed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="all" className="data-[state=active]:bg-gray-800">
              All ({stats.total})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-gray-800">
              Pending ({stats.pending_count})
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-gray-800">
              Completed ({stats.completed_count})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Bookings List */}
        {bookings.length === 0 ? (
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No bookings yet</h3>
              <p className="text-gray-400">Bookings will appear here when clients schedule consultations.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card 
                key={booking.booking_id} 
                className={`bg-gray-900/50 border-gray-800 transition-all ${
                  booking.status === 'completed' ? 'opacity-70' : ''
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Booking Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          booking.status === 'completed' 
                            ? 'bg-emerald-500/10' 
                            : 'bg-amber-500/10'
                        }`}>
                          {booking.status === 'completed' ? (
                            <CheckCircle className="w-6 h-6 text-emerald-400" />
                          ) : (
                            <Calendar className="w-6 h-6 text-amber-400" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-white">
                              {booking.client_name}
                            </h3>
                            <Badge className={
                              booking.status === 'completed'
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            }>
                              {booking.status === 'completed' ? 'Completed' : 'Pending'}
                            </Badge>
                          </div>
                          
                          <p className="text-amber-400 font-medium mb-2">
                            {booking.consultation_name}
                          </p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                            <div className="flex items-center gap-2 text-gray-400">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(booking.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                              <Clock className="w-4 h-4" />
                              <span>{booking.time_slot}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                              <Mail className="w-4 h-4" />
                              <span className="truncate">{booking.client_email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                              <Phone className="w-4 h-4" />
                              <span>{booking.client_phone}</span>
                            </div>
                          </div>
                          
                          {booking.message && (
                            <p className="text-gray-500 text-sm mt-2 italic">
                              "{booking.message}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 lg:flex-col lg:items-end">
                      <p className="text-xl font-bold text-amber-400 mb-2">
                        ${booking.price}
                      </p>
                      
                      {booking.status !== 'completed' ? (
                        <Button
                          onClick={() => handleMarkComplete(booking.booking_id, true)}
                          disabled={processingId === booking.booking_id}
                          className="bg-emerald-600 hover:bg-emerald-500"
                          size="sm"
                        >
                          {processingId === booking.booking_id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Done & Send Email
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleReopen(booking.booking_id)}
                          disabled={processingId === booking.booking_id}
                          variant="outline"
                          className="border-gray-700 text-gray-300 hover:bg-gray-800"
                          size="sm"
                        >
                          {processingId === booking.booking_id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <RotateCcw className="w-4 h-4 mr-2" />
                          )}
                          Reopen
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => handleDelete(booking.booking_id)}
                        disabled={processingId === booking.booking_id}
                        variant="outline"
                        className="border-red-900 text-red-400 hover:bg-red-500/10"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingsList;
